import { useEffect, useRef, useState } from 'react';
import type { DemoScript, Fx, Level, RoundProps, ViewProps } from '../kit.js';
import { BOARD, BoardButton, BoardFrame, INK, OK, RADIUS, STROKE, TYPE, WARN, alpha, clamp, easier, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';

/**
 * SIGNAL OFFICER · MATCH THE WAVE — slide the controls until your wave lies on the dashed one.
 *
 * A scope on the signal desk shows the wave to catch (dashed) and the wave you are sending (solid),
 * both rolling gently along. Two big faders set your wave's HEIGHT and LENGTH: touch a fader's row
 * anywhere and its cap jumps under the finger, then slide. Each has a lamp that turns amber when it
 * is close and green when it is right, and its cap turns green with it. With every control right,
 * holding still for a moment locks the wave; the bar along the foot of the scope fills while it
 * holds. There is nothing to get wrong. Each round wants a closer match and a longer hold. (A third
 * fader, SHIFT, that slides the wave sideways into step, the old round 3, is still in the rules; no
 * round deals it since 6 Oct 2026.)
 *
 * A converted board (kit.tsx): the rules as plain functions, a small view state (`WaveView`), a
 * pure `View` with the faders' caps as the kit's buttons, the `Board` that plays it, and the `demo`.
 */

export interface Knobs {
  height: number;
  length: number;
  shift: number;
}

export interface WaveSpec {
  /** How close each control must be, as a share of its travel. */
  tol: number;
  /** How long every control must stay right to lock. */
  holdMs: number;
  /** A third fader, SHIFT. */
  shift: boolean;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const WAVES: readonly WaveSpec[] = easier(
  [
    { tol: 0.07, holdMs: 600, shift: false },
    { tol: 0.05, holdMs: 800, shift: false },
  ],
);

/** A control's lamp turns amber within this many times its tolerance. */
export const NEAR = 2.5;

/** How far a control starts from its target, at the least (shift, being round, a little less). */
export const START_OFF = 0.3;

/** The target, and where the faders start: every one of them well away from it. */
export function waveDeal(level: Level, rand: () => number): { target: Knobs; start: Knobs } {
  const spec = WAVES[level]!;
  const target: Knobs = { height: 0.15 + rand() * 0.7, length: 0.15 + rand() * 0.7, shift: spec.shift ? rand() : 0 };
  const away = (goal: number, round: boolean) => {
    for (let i = 0; i < 60; i++) {
      const v = 0.04 + rand() * 0.92;
      if (knobOff(v, goal, round) >= (round ? START_OFF - 0.05 : START_OFF)) return v;
    }
    return round ? (goal + 0.5) % 1 : goal > 0.5 ? 0.04 : 0.96;
  };
  return {
    target,
    start: { height: away(target.height, false), length: away(target.length, false), shift: spec.shift ? away(target.shift, true) : 0 },
  };
}

/** How far one control is from its target; SHIFT goes round, so 0.95 is close to 0.05. */
export function knobOff(value: number, goal: number, round = false): number {
  const d = Math.abs(value - goal);
  return round ? Math.min(d % 1, 1 - (d % 1)) : d;
}

/** How far each control is from the target. */
export function waveOff(mine: Knobs, target: Knobs): Knobs {
  return {
    height: knobOff(mine.height, target.height),
    length: knobOff(mine.length, target.length),
    shift: knobOff(mine.shift, target.shift, true),
  };
}

/** Every control the round uses is close enough. */
export function waveMatched(off: Knobs, spec: WaveSpec): boolean {
  return off.height <= spec.tol && off.length <= spec.tol && (!spec.shift || off.shift <= spec.tol);
}

/** The wave's height at `u` (0–1 across the scope), `t` seconds in: −1 to 1. Both waves roll along together. */
export function waveY(k: Knobs, u: number, t: number): number {
  const amplitude = 0.18 + 0.74 * k.height;
  const cycles = 1 + 3 * (1 - k.length);
  return amplitude * Math.sin(Math.PI * 2 * (cycles * u + k.shift) - t * 1.1);
}

/** A control's lamp: right, close, or not yet. */
export function lampOf(off: number, tol: number): 'on' | 'near' | 'off' {
  return off <= tol ? 'on' : off <= tol * NEAR ? 'near' : 'off';
}

type Control = keyof Knobs;
const CONTROLS: readonly Control[] = ['height', 'length', 'shift'];

/** Everything the desk shows. */
export interface WaveView {
  /** The game clock, ms: both waves roll along with it. */
  t: number;
  /** The wave to catch: height, length, shift (0–1 each). */
  g: readonly [number, number, number];
  /** Yours, the same three. */
  m: readonly [number, number, number];
  /** The third fader, SHIFT, is in play. */
  sh: boolean;
  /** How close each control must be. */
  tol: number;
  /** The hold, 0–1 of what locks it. */
  h: number;
  /** The wave is locked. */
  lock: boolean;
  /** The fader under the finger, or −1. */
  d: number;
  fx?: Fx | undefined;
}

const W = BOARD.w;
const H = BOARD.h;
/** The faders' caps travel between these, across the board. */
const TX0 = 112;
const TX1 = 286;
const CAP_W = 36;

/** Where everything sits, with two faders or three: the scope takes what the faders leave. */
function desk(three: boolean) {
  const rowH = three ? 56 : 66;
  const gap = 4;
  const rows = three ? 3 : 2;
  const panelTop = H - 8 - rows * rowH - (rows - 1) * gap - 8;
  return {
    rowH,
    panelTop,
    scope: { x: 10, y: 8, w: W - 20, h: panelTop - 16 },
    /** The middle of fader `i`'s row, down the board. */
    row: (i: number) => panelTop + 4 + i * (rowH + gap) + rowH / 2,
  };
}

/** Where a cap sits across the board for a value 0–1, and the value under a finger. */
export const capX = (v: number) => TX0 + clamp(v, 0, 1) * (TX1 - TX0);
export const faderValue = (x: number) => clamp((x - TX0) / (TX1 - TX0), 0, 1);

/** Which fader's row a finger is on (its whole row, label and all), or −1. */
export function faderAt(y: number, three: boolean): number {
  const { rowH, row } = desk(three);
  const rows = three ? 3 : 2;
  for (let i = 0; i < rows; i++) if (Math.abs(y - row(i)) <= rowH / 2 + 2) return i;
  return -1;
}

function wavePath(k: Knobs, t: number, box: { x: number; y: number; w: number; h: number }): string {
  const mid = box.y + box.h / 2;
  const amp = box.h / 2 - 12;
  const points = 90;
  let d = '';
  for (let i = 0; i < points; i++) {
    const u = i / (points - 1);
    const x = box.x + 10 + u * (box.w - 20);
    const y = mid - amp * waveY(k, u, t);
    d += `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)} `;
  }
  return d;
}

const knobsOf = (v: readonly [number, number, number]): Knobs => ({ height: v[0], length: v[1], shift: v[2] });
const LAMP = { on: OK, near: WARN, off: alpha(INK.hi, 0.14) } as const;

/** The desk, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<WaveView>) {
  const skin = ERA_SKIN[era];
  const id = useSvgId();
  const { scope, rowH, panelTop, row } = desk(view.sh);
  const t = view.t / 1000;
  const target = knobsOf(view.g);
  const mine = view.lock ? target : knobsOf(view.m);
  const off = waveOff(mine, target);
  const used = view.sh ? CONTROLS : CONTROLS.slice(0, 2);
  const lamps = used.map((c) => (view.lock ? 'on' : lampOf(off[c], view.tol)));
  const right = lamps.filter((l) => l === 'on').length;
  const matched = view.lock || right === used.length;
  const held = view.lock ? 1 : clamp(view.h, 0, 1);
  const dragging = Math.round(view.d);
  const names: Record<Control, string> = {
    height: say(lang, 'HEIGHT', 'HØYDE'),
    length: say(lang, 'LENGTH', 'LENGDE'),
    shift: say(lang, 'SHIFT', 'FLYTT'),
  };
  const status = view.lock
    ? { text: say(lang, '✓ WAVE LOCKED', '✓ BØLGEN LÅST'), tone: 'ok' as const }
    : matched
      ? { text: say(lang, 'SPOT ON — HOLD STILL', 'TREFF – HOLD STILLE'), tone: 'ok' as const }
      : { text: say(lang, 'GREEN', 'GRØNNE'), count: [right, used.length] as const, hint: say(lang, 'SLIDE TO MATCH', 'SKYV TIL TREFF') };
  const yours = matched ? INK.hi : accent;

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <radialGradient id={`${id}s`} cx=".5" cy=".5" r=".75">
          <stop offset="0" stopColor={skin.ink} stopOpacity=".18" />
          <stop offset="1" stopColor={INK.onFill} stopOpacity=".96" />
        </radialGradient>
        <clipPath id={`${id}c`}>
          <rect x={scope.x} y={scope.y} width={scope.w} height={scope.h} rx={RADIUS.button} />
        </clipPath>
      </defs>

      {/* the scope: the target, dashed, and yours over it */}
      <rect x={scope.x} y={scope.y} width={scope.w} height={scope.h} rx={RADIUS.button} fill={`url(#${id}s)`} stroke={alpha(skin.ink, 0.4)} strokeWidth={STROKE.hair} />
      <g clipPath={`url(#${id}c)`}>
        {Array.from({ length: 13 }, (_, i) => {
          const x = scope.x + 10 + ((i + 1) * (scope.w - 20)) / 14;
          return <line key={`v${i}`} x1={x} y1={scope.y} x2={x} y2={scope.y + scope.h} stroke={skin.haze} strokeOpacity=".07" />;
        })}
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={scope.x} y1={scope.y + f * scope.h} x2={scope.x + scope.w} y2={scope.y + f * scope.h} stroke={skin.haze} strokeOpacity={f === 0.5 ? 0.16 : 0.07} />
        ))}
        <path d={wavePath(target, t, scope)} fill="none" stroke={INK.hi} strokeOpacity=".85" strokeWidth="2.2" strokeDasharray="7 6" strokeLinecap="round" />
        <path d={wavePath(mine, t, scope)} fill="none" stroke={yours} strokeOpacity=".2" strokeWidth="9" strokeLinejoin="round" strokeLinecap="round" />
        <path d={wavePath(mine, t, scope)} fill="none" stroke={yours} strokeWidth={STROKE.bold} strokeLinejoin="round" strokeLinecap="round" />
        {/* the hold, filling along the foot */}
        {held > 0 && <rect x={scope.x} y={scope.y + scope.h - 5} width={scope.w * held} height="5" fill={view.lock ? OK : accent} opacity=".9" />}
      </g>
      <g fontFamily={mono} fontSize={TYPE.hint} fill={INK.text} opacity=".8">
        <line x1={scope.x + 10} y1={scope.y + 14} x2={scope.x + 28} y2={scope.y + 14} stroke={INK.hi} strokeWidth="2" strokeDasharray="4 3" />
        <text x={scope.x + 33} y={scope.y + 18}>{say(lang, 'TARGET', 'MÅL')}</text>
        <line x1={scope.x + 96} y1={scope.y + 14} x2={scope.x + 114} y2={scope.y + 14} stroke={accent} strokeWidth={STROKE.bold} />
        <text x={scope.x + 119} y={scope.y + 18}>{say(lang, 'YOURS', 'DIN')}</text>
      </g>

      {/* the faders: a name and a lamp, the groove, and a cap that turns green when it is right */}
      <rect x="10" y={panelTop} width={W - 20} height={H - 8 - panelTop} rx={RADIUS.button} fill={alpha(INK.onFill, 0.8)} stroke={alpha(skin.ink, 0.18)} strokeWidth={STROKE.hair} />
      {used.map((c, i) => {
        const y = row(i);
        const lamp = lamps[i]!;
        const v = clamp(c === 'height' ? mine.height : c === 'length' ? mine.length : mine.shift, 0, 1);
        return (
          <g key={c}>
            <circle cx="24" cy={y} r="6" fill={LAMP[lamp]} stroke={alpha(INK.hi, 0.2)} />
            {lamp !== 'off' && <circle cx="24" cy={y} r="10" fill={LAMP[lamp]} opacity=".2" />}
            <text x="36" y={y + TYPE.hint * 0.36} fontFamily={mono} fontSize={TYPE.hint} fontWeight="700" letterSpacing="1.2" fill={skin.haze} opacity=".85">
              {names[c]}
            </text>
            <rect x={TX0 - 6} y={y - 4} width={TX1 - TX0 + 12} height="8" rx="4" fill={INK.onFill} stroke={alpha(skin.ink, 0.25)} strokeWidth={STROKE.hair} />
            {Array.from({ length: 11 }, (_, k) => {
              const x = TX0 + (k / 10) * (TX1 - TX0);
              return <line key={k} x1={x} y1={y + 9} x2={x} y2={y + (k % 5 === 0 ? 17 : 13)} stroke={skin.haze} strokeOpacity=".35" />;
            })}
            <BoardButton
              x={capX(v) - CAP_W / 2}
              y={y - Math.max(44, rowH - 14) / 2}
              w={CAP_W}
              h={Math.max(44, rowH - 14)}
              label={names[c]}
              accent={accent}
              tone={lamp === 'on' ? 'ok' : 'plain'}
              down={dragging === i}
            >
              <rect x="-2" y="-12" width="4" height="24" rx="2" fill={lamp === 'on' ? OK : lamp === 'near' ? WARN : accent} />
            </BoardButton>
          </g>
        );
      })}
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, lang } = props;
  const spec = WAVES[level]!;
  const [deal] = useState(() => waveDeal(level, seeded(Date.now() + level * 15_485_863)));
  const used = spec.shift ? CONTROLS : CONTROLS.slice(0, 2);
  const knobs = useRef<Knobs>({ ...deal.start });
  const hold = useRef(0);
  const best = useRef(0);
  /** The fader in the finger, or null. */
  const grab = useRef<number | null>(null);

  const round = useRound(props, (dt, _now, won) => {
    if (won) return;
    const off = waveOff(knobs.current, deal.target);
    hold.current = waveMatched(off, spec) ? hold.current + dt * 1000 : Math.max(0, hold.current - dt * 2000);
    // Progress: how close the controls are, then the hold.
    const closeness = used.reduce((s, c) => s + Math.max(0, 1 - Math.max(0, off[c] - spec.tol) / START_OFF), 0) / used.length;
    const progress = Math.min(0.99, closeness * 0.8 + (hold.current / spec.holdMs) * 0.2);
    if (progress > best.current + 0.02) {
      best.current = progress;
      round.progress(progress);
    }
    if (hold.current >= spec.holdMs) round.win();
  });

  useEffect(() => {
    if (props.paused) grab.current = null;
  }, [props.paused]);

  const slide = (x: number) => {
    const c = grab.current === null ? undefined : used[grab.current];
    if (!c || !round.live) return;
    knobs.current = { ...knobs.current, [c]: faderValue(x) };
  };

  const mine = round.won ? deal.target : knobs.current;
  const view: WaveView = {
    t: q(round.now),
    g: [q(deal.target.height, 3), q(deal.target.length, 3), q(deal.target.shift, 3)],
    m: [q(mine.height, 3), q(mine.length, 3), q(mine.shift, 3)],
    sh: spec.shift,
    tol: spec.tol,
    h: round.won ? 1 : q(Math.min(1, hold.current / spec.holdMs), 2),
    lock: round.won,
    d: grab.current ?? -1,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{
        label: say(lang, 'Slide the controls to match the wave', 'Skyv kontrollene for å treffe bølgen'),
        down: (p) => {
          if (!round.live) return;
          const i = faderAt(p.y, spec.shift);
          if (i < 0) return;
          grab.current = i;
          slide(p.x);
        },
        move: (p) => slide(p.x),
        up: () => {
          grab.current = null;
        },
      }}
    />
  );
}

/** The how-to's round 1: the target, where the faders start, and when the match is made and the wave locks. */
const DEMO_GOAL = [0.72, 0.3, 0] as const;
const DEMO_FROM = [0.22, 0.78, 0] as const;
const DEMO_TOL = WAVES[0]!.tol;
const DEMO_HOLD = WAVES[0]!.holdMs;
/** The second slide runs from 3 000 to 4 300 ms; it comes inside the tolerance on the way. */
const DEMO_MATCH = Math.round(3_000 + ((DEMO_FROM[1] - DEMO_GOAL[1] - DEMO_TOL) / (DEMO_FROM[1] - DEMO_GOAL[1])) * 1_300);
const DEMO_LOCK = DEMO_MATCH + DEMO_HOLD;
const DEMO_ROW = desk(false).row;

/**
 * The how-to: the finger slides HEIGHT until its lamp is green, then LENGTH until its lamp is
 * green too; the two waves lie together, the bar fills, and the wave locks.
 */
const demo: DemoScript<WaveView> = {
  ms: 7_000,
  keys: [
    { at: 0, view: { t: 0, g: DEMO_GOAL, m: DEMO_FROM, sh: false, tol: DEMO_TOL, h: 0, lock: false, d: -1 } },
    { at: 900, view: { d: 0 }, ease: 'step' },
    { at: 2_300, view: { m: [DEMO_GOAL[0], DEMO_FROM[1], 0] } },
    { at: 2_400, view: { d: -1 }, ease: 'step' },
    { at: 3_000, view: { d: 1 }, ease: 'step' },
    { at: DEMO_MATCH, view: { m: [DEMO_GOAL[0], DEMO_GOAL[1] + DEMO_TOL, 0] } },
    { at: 4_300, view: { m: DEMO_GOAL, h: q((4_300 - DEMO_MATCH) / DEMO_HOLD, 2) } },
    { at: 4_400, view: { d: -1 }, ease: 'step' },
    { at: DEMO_LOCK, view: { h: 1 } },
    { at: DEMO_LOCK + 1, view: { lock: true, fx: [1, 0] }, ease: 'step' },
    { at: DEMO_LOCK + 450, view: { fx: [0, 0] } },
  ],
  finger: [
    { at: 0, x: capX(DEMO_FROM[0]), y: DEMO_ROW(0), hide: true },
    { at: 400, x: capX(DEMO_FROM[0]), y: DEMO_ROW(0), hide: false },
    { at: 900, x: capX(DEMO_FROM[0]), y: DEMO_ROW(0), down: true },
    { at: 2_300, x: capX(DEMO_GOAL[0]), y: DEMO_ROW(0), ease: 'linear' },
    { at: 2_400, x: capX(DEMO_GOAL[0]), y: DEMO_ROW(0), down: false },
    { at: 2_850, x: capX(DEMO_FROM[1]), y: DEMO_ROW(1) },
    { at: 3_000, x: capX(DEMO_FROM[1]), y: DEMO_ROW(1), down: true },
    { at: 4_300, x: capX(DEMO_GOAL[1]), y: DEMO_ROW(1), ease: 'linear' },
    { at: 4_400, x: capX(DEMO_GOAL[1]), y: DEMO_ROW(1), down: false },
    { at: 5_200, x: capX(DEMO_GOAL[1]) + 30, y: DEMO_ROW(1) + 20, hide: true },
  ],
  steps: [
    { at: 0, en: 'SLIDE UNTIL THE LAMP IS GREEN', no: 'SKYV TIL LAMPEN ER GRØNN' },
    { at: 2_500, en: 'THEN THE NEXT ONE', no: 'SÅ DEN NESTE' },
    { at: DEMO_MATCH, en: 'ALL GREEN: HOLD STILL', no: 'ALT GRØNT: HOLD STILLE' },
  ],
};

export const game = thirtyGame<WaveView>({
  Board,
  View,
  demo,
  // Nothing to get wrong here: no mistake count under the board.
  mistakes: false,
  words: {
    en: {
      instruction: 'Slide the controls until your wave lies on the dashed one, then hold still.',
      howMove: 'Slide each control left or right. Its lamp turns green when it is right.',
    },
    no: {
      instruction: 'Skyv kontrollene til bølgen din ligger oppå den stiplede, og hold stille.',
      howMove: 'Skyv hver kontroll til venstre eller høyre. Lampen blir grønn når den er riktig.',
    },
  },
});
